import { useState } from 'react';

export default function SSLCertificatesSection() {
    const [activeStep, setActiveStep] = useState(1);

    const steps = [
        {
            id: 1,
            label: "Step 1",
            heading: "Connect SSL to your domain",
            description: "SSL (Secure Sockets Layer) is a method, or protocol used for establishing a secure encrypted connection between a browser (user's computer) and a server (hosting your website).",
            icon: (
                <div className="w-16 h-20 bg-white rounded-lg shadow-2xl overflow-hidden">
                    <div className="h-4 bg-gradient-to-r from-blue-500 to-blue-600 flex items-center px-2 gap-0.5">
                        <div className="w-1 h-1 rounded-full bg-white"></div>
                        <div className="w-1 h-1 rounded-full bg-white"></div>
                        <div className="flex-1 h-2 bg-white/30 rounded ml-0.5"></div>
                    </div>
                    <div className="p-1.5 space-y-1">
                        <div className="flex items-center gap-1">
                            <svg viewBox="0 0 24 24" className="w-1.5 h-1.5 text-blue-500" fill="currentColor">
                                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                            </svg>
                            <span className="text-[6px] font-bold text-blue-600">.com</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <svg viewBox="0 0 24 24" className="w-1.5 h-1.5 text-blue-500" fill="currentColor">
                                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                            </svg>
                            <span className="text-[6px] font-bold text-blue-600">.net</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <svg viewBox="0 0 24 24" className="w-1.5 h-1.5 text-blue-500" fill="currentColor">
                                <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
                            </svg>
                            <span className="text-[6px] font-bold text-blue-600">.org</span>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 2,
            label: "Step 2",
            heading: "Connect SSL to your domain",
            description: "Once you have purchased your SSL Certificate, we will connect it to your domain name to ensure your website is secure. If your website is hosted with another provider we will provide you the certificate so you can install with them.",
            icon: (
                <div className="w-16 h-20 bg-white rounded-lg shadow-2xl p-2 flex flex-col items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="mt-0.5 text-[5px] font-bold text-gray-500">SSL CERT</div>
                </div>
            )
        },
        {
            id: 3,
            label: "Step 3",
            heading: "HTTPS",
            description: "After the SSL certificate is installed in the web server, your website can now be visited under HTTPS (S' stands for 'secure') and a padlock icon will appear beside your website's URL.",
            icon: (
                <div className="w-16 h-20 bg-white rounded-lg shadow-2xl overflow-hidden">
                    <div className="h-4 bg-gradient-to-r from-blue-500 to-blue-600 flex items-center px-2 gap-0.5">
                        <div className="w-1 h-1 rounded-full bg-white"></div>
                        <div className="w-1 h-1 rounded-full bg-white"></div>
                        <div className="flex-1 h-2 bg-white/30 rounded ml-0.5"></div>
                    </div>
                    <div className="p-1.5 flex flex-col items-center justify-center">
                        <div className="text-base font-bold text-blue-500 mb-0.5">www</div>
                        <div className="flex items-center gap-0.5">
                            <div className="w-5 h-0.5 bg-gray-200 rounded"></div>
                            <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-green-500" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                            </svg>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 4,
            label: "Step 4",
            heading: "Enjoy increased SEO",
            description: "Enjoy increased SEO and page ranking with Google, and improved trust from your clients knowing that they can supply their information to you securely.",
            icon: (
                <div className="w-16 h-20 bg-white rounded-lg shadow-2xl flex items-center justify-center">
                    <div className="relative">
                        <svg viewBox="0 0 24 24" className="w-10 h-10 text-green-500" fill="currentColor">
                            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                        </svg>
                    </div>
                </div>
            )
        }
    ];

    const currentStep = steps.find(s => s.id === activeStep);

    return (
        <>
            <style>{`
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .hover-scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>

            <div className="ssl-section min-h-screen py-2 md:py-8 lg:py-12">
                <div className="container">

                    {/* Mobile View (< 768px) */}
                    <div className="md:hidden">
                        <div className="text-center mb-12">
                            <h1 className="text-3xl font-bold text-white mb-6 leading-tight">
                                How do SSL<br />Certificates work?
                            </h1>

                            <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 mb-8">
                                <p className="text-sm text-gray-200 leading-relaxed">
                                    {currentStep.description}
                                </p>
                            </div>

                            {/* Mobile Step Visual */}
                            <div className="flex flex-col items-center mb-8">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-2xl animate-pulse-slow"></div>
                                    <div className="relative w-36 h-36 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full shadow-2xl flex items-center justify-center">
                                        {currentStep.icon}
                                    </div>
                                </div>

                                {/* Mobile Step Indicators */}
                                <div className="flex items-center gap-3 mt-8">
                                    {steps.map((step) => (
                                        <button
                                            key={step.id}
                                            onClick={() => setActiveStep(step.id)}
                                            className={`transition-all duration-300 ${activeStep === step.id
                                                    ? 'w-12 h-12 border-3'
                                                    : 'w-10 h-10 border-2'
                                                }`}
                                            style={{
                                                borderRadius: '50%',
                                                borderColor: activeStep === step.id ? '#3B82F6' : '#374151',
                                                backgroundColor: activeStep === step.id ? '#000000e0' : '#1F2937',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: activeStep === step.id ? '#ffffff' : '#9CA3AF',
                                                fontWeight: activeStep === step.id ? 'bold' : 'normal',
                                                fontSize: activeStep === step.id ? '16px' : '14px'
                                            }}
                                        >
                                            {step.id}
                                        </button>
                                    ))}
                                </div>

                                {/* Mobile Navigation */}
                                <div className="flex items-center gap-3 mt-8 w-full max-w-xs">
                                    <button
                                        onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                                        disabled={activeStep === 1}
                                        className={`flex-1 py-3 rounded-lg font-semibold transition-all ${activeStep === 1
                                                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                                : 'bg-gray-700 text-white hover:bg-gray-600'
                                            }`}
                                    >
                                        ← Prev
                                    </button>

                                    <button
                                        onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
                                        disabled={activeStep === 4}
                                        className={`flex-1 py-3 rounded-lg font-semibold transition-all ${activeStep === 4
                                                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tablet View (768px - 1023px) */}
                    <div className="hidden md:block lg:hidden">
                        <div className="text-center mb-12">
                            <h1 className="text-4xl font-bold text-white mb-6">
                                How do SSL Certificates work?
                            </h1>
                            <p className="text-lg font-semibold text-gray-300 mb-3">
                                SSL (Secure Sockets Layer)
                            </p>
                            <p className="text-base text-gray-500 max-w-3xl mx-auto leading-relaxed">
                                SSL (Secure Sockets Layer) is a method, or protocol used for establishing a secure encrypted connection between a browser (user's computer) and a server (hosting your website).
                            </p>
                        </div>

                        {/* Tablet 2x2 Grid */}
                        <div className="grid grid-cols-2 gap-8 mb-12">
                            {steps.map((step) => (
                                <button
                                    key={step.id}
                                    onClick={() => setActiveStep(step.id)}
                                    className={`group transition-all duration-500 hover-scale-102 ${activeStep === step.id ? 'scale-105' : ''
                                        }`}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="relative mb-4">
                                            {activeStep === step.id && (
                                                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full blur-2xl animate-pulse-slow"></div>
                                            )}
                                            <div className={`relative w-36 h-36 rounded-full flex items-center justify-center transition-all duration-500 ${activeStep === step.id
                                                    ? 'bg-gradient-to-br from-gray-600 to-gray-700 shadow-2xl'
                                                    : 'bg-gradient-to-br from-gray-700 to-gray-800 shadow-lg'
                                                }`}>
                                                {step.icon}
                                            </div>
                                        </div>
                                        <p className={`text-sm font-semibold transition-colors ${activeStep === step.id ? 'text-white' : 'text-gray-300'
                                            }`}>
                                            {step.label}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Tablet Content Card */}
                        <div className="mb-12">
                            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-lg rounded-3xl p-10 border border-gray-700/50 shadow-2xl">
                                <div className="flex items-start justify-between mb-6">
                                    <div>
                                        <p className="text-sm text-gray-300 font-semibold mb-2">{currentStep.label}</p>
                                        <h3 className="text-2xl font-bold text-white mb-2">{currentStep.heading}</h3>
                                    </div>
                                    <div className="text-sm text-gray-500 font-medium bg-gray-800 px-4 py-2 rounded-lg">
                                        {activeStep} / 4
                                    </div>
                                </div>

                                <p className="text-gray-300 text-base leading-relaxed mb-8">
                                    {currentStep.description}
                                </p>

                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                                        disabled={activeStep === 1}
                                        className={`flex-1 py-3 rounded-xl font-semibold transition-all ${activeStep === 1
                                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                                : 'bg-gray-700 text-white hover:bg-gray-600 hover:shadow-lg'
                                            }`}
                                    >
                                        ← Previous
                                    </button>

                                    <button
                                        onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
                                        disabled={activeStep === 4}
                                        className={`flex-1 py-3 rounded-xl font-semibold transition-all ${activeStep === 4
                                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-xl hover:shadow-blue-500/30'
                                            }`}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop View (>= 1024px) */}
                    <div className="hidden lg:block">
                        <div className="text-center mb-16">
                            <h1 className="text-3xl xl:text-4xl font-bold text-white mb-4">
                                How do SSL Certificates work?
                            </h1>
                        </div>

                        {/* Desktop Steps */}
                        <div className="relative mb-16">
                            {/* Connection Lines */}
                            <svg className="absolute top-1/2 left-0 w-full h-1 pointer-events-none" style={{ transform: 'translateY(-50%)' }}>
                                <line x1="15%" y1="0" x2="35%" y2="0" stroke="#374151" strokeWidth="2" strokeDasharray="8,8" opacity="0.5" />
                                <line x1="40%" y1="0" x2="60%" y2="0" stroke="#374151" strokeWidth="2" strokeDasharray="8,8" opacity="0.5" />
                                <line x1="65%" y1="0" x2="85%" y2="0" stroke="#374151" strokeWidth="2" strokeDasharray="8,8" opacity="0.5" />
                            </svg>

                            <div className="grid grid-cols-4 gap-8 relative z-10">
                                {steps.map((step) => (
                                    <div key={step.id} className="flex flex-col items-center">
                                        <button
                                            onClick={() => setActiveStep(step.id)}
                                            className={`group transition-all duration-500 ${activeStep === step.id ? 'scale-110' : 'hover:scale-105'
                                                }`}
                                        >
                                            <div className="relative mb-4">
                                                {activeStep === step.id && (
                                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full blur-2xl animate-pulse-slow"></div>
                                                )}
                                                <div className={`relative w-40 h-40 rounded-full flex items-center justify-center transition-all duration-500 ${activeStep === step.id
                                                        ? 'bg-gradient-to-br from-gray-600 to-gray-700 shadow-2xl'
                                                        : 'bg-gradient-to-br from-gray-700 to-gray-800 shadow-lg'
                                                    }`}>
                                                    {step.icon}
                                                </div>
                                            </div>
                                            <p className={`text-sm font-semibold transition-colors ${activeStep === step.id ? 'text-white' : 'text-gray-300'
                                                }`}>
                                                {step.label}
                                            </p>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Desktop Content Card */}
                        <div className="mb-8">
                            <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-xl rounded-2xl p-12 border border-purple-500/20 shadow-2xl">
                                <div className="flex items-start justify-between mb-6">
                                    <div>
                                        <p className="text-sm text-gray-300 font-semibold mb-2">{currentStep.label}</p>
                                        <h3 className="text-3xl font-bold text-white mb-0">{currentStep.heading}</h3>
                                    </div>
                                    <div className="text-sm text-gray-500 font-medium bg-gray-800/50 px-4 py-2 rounded-lg border border-gray-700/50">
                                        {activeStep} / 4
                                    </div>
                                </div>

                                <p className="text-gray-300 text-lg leading-relaxed mb-8">
                                    {currentStep.description}
                                </p>

                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                                        disabled={activeStep === 1}
                                        className={`px-8 py-3 rounded-xl font-semibold transition-all ${activeStep === 1
                                                ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                                                : 'bg-gray-700 text-white hover:bg-gray-600 hover:shadow-lg'
                                            }`}
                                    >
                                        ← Previous
                                    </button>

                                    <button
                                        onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
                                        disabled={activeStep === 4}
                                        className={`px-8 py-3 rounded-xl font-semibold transition-all ${activeStep === 4
                                                ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-xl hover:shadow-blue-500/30'
                                            }`}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}